/**
 * Push-to-talk.
 *
 * Opens the phone microphone only while the button is held, adds it to the
 * existing peer connection, and removes it on release. This is a separate path
 * from M.Z: M.Z stays the PC's own audio-only microphone and is not involved,
 * replaced or reconfigured here.
 */

export class Talk {
  private stream: MediaStream | null = null;
  private turn: string | null = null;

  get active(): boolean {
    return this.turn !== null;
  }

  get turnId(): string | null {
    return this.turn;
  }

  get track(): MediaStreamTrack | null {
    return this.stream?.getAudioTracks()[0] ?? null;
  }

  /**
   * Acquires the microphone and returns the turn id. Echo cancellation is on
   * because the PC will be speaking replies into the same room.
   */
  async begin(): Promise<{ turnId: string; track: MediaStreamTrack }> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("Ce navigateur n'expose pas le microphone.");
    }
    if (this.turn) throw new Error('Un tour de parole est déjà en cours.');

    this.stream = await navigator.mediaDevices.getUserMedia({
      video: false,
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      },
    });

    const track = this.stream.getAudioTracks()[0];
    if (!track) {
      this.release();
      throw new Error('Aucune piste audio disponible.');
    }

    this.turn = crypto.randomUUID();
    return { turnId: this.turn, track };
  }

  /** Ends the turn and frees the microphone. Returns the id that just ended. */
  end(): string | null {
    const ended = this.turn;
    this.turn = null;
    this.release();
    return ended;
  }

  private release(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }
}
